import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame, LogIn, UserPlus, Loader2, ShieldCheck, KeyRound, MailCheck, ArrowLeft } from "lucide-react";

const errText = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  return String(detail?.msg || detail);
};

const TITLES = {
  login: "Sign in",
  register: "Create account",
  setup: "Create owner account",
  forgot: "Reset password",
  reset: "Choose a new password",
};

export default function LoginScreen() {
  const { status, login, register, resetPassword } = useAuth();
  const setupMode = !!status?.needs_setup;
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("reset_token") || "";
  const inviteToken = params.get("invite") || "";

  const [mode, setMode] = useState(resetToken ? "reset" : inviteToken ? "register" : setupMode ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);

  const canRegister = setupMode || !!status?.allow_signups || !!invite?.valid;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!inviteToken) return;
    api.checkInvite(inviteToken)
      .then((d) => {
        setInvite(d);
        if (d.valid) { setForm((f) => ({ ...f, email: d.email })); setMode("register"); }
        else { setError(d.reason || "This invitation is no longer valid"); setMode("login"); }
      })
      .catch(() => setInvite({ valid: false }));
  }, [inviteToken]);

  const clean = () => window.history.replaceState({}, "", window.location.pathname);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setSent(null);
    try {
      if (mode === "forgot") {
        const r = await api.forgotPassword(form.email.trim());
        setSent(r);
      } else if (mode === "reset") {
        await resetPassword(resetToken, form.password);
        clean();
      } else if (mode === "register") {
        await register(form.email, form.password, form.name, inviteToken);
        clean();
      } else {
        await login(form.email, form.password);
      }
    } catch (err) {
      setError(errText(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const heading = setupMode && mode === "register" ? TITLES.setup : TITLES[mode];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#04060A] text-slate-200">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="h-10 w-10 rounded-md bg-cyan-500/15 border border-cyan-500/40 grid place-items-center">
            <Flame className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-xl leading-none">DATAFORGE <span className="text-cyan-400">AI</span></h1>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">Dataset Foundry</p>
          </div>
        </div>
        <div className="relative space-y-6 max-w-md">
          <h2 className="font-display font-bold text-lg">Crawl the web. Forge the dataset. Train the model.</h2>
          <ul className="space-y-2.5 text-sm text-slate-400">
            {[
              "Keyword, seed-URL and sitemap crawling that runs until you stop it",
              "Corpus quality grading, versions and one-click improvements",
              "Bring your own OpenAI, Gemini, Claude or local Ollama keys",
            ].map((t) => (
              <li key={t} className="flex gap-2"><span className="text-cyan-500">›</span>{t}</li>
            ))}
          </ul>
        </div>
        <p className="relative text-[11px] font-mono text-slate-700">your keys stay encrypted server-side · never sent back to the browser</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-2 mb-2">
            <Flame className="h-5 w-5 text-cyan-400" />
            <span className="font-display font-extrabold">DATAFORGE AI</span>
          </div>

          <div>
            <h2 className="font-mono font-extrabold text-2xl uppercase tracking-tight" data-testid="auth-heading">{heading}</h2>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "forgot" ? "We'll email you a single-use link to set a new password."
                : mode === "reset" ? "Pick something at least 8 characters long."
                : invite?.valid ? `Invitation accepted for ${invite.email} — set a password to finish.`
                : setupMode ? "First account becomes the owner and inherits any existing datasets."
                : mode === "register" ? "Your crawls and datasets stay private to your account."
                : "Welcome back. Your datasets are waiting."}
            </p>
          </div>

          {mode === "register" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Name <span className="text-slate-600">(optional)</span></Label>
              <Input data-testid="auth-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                className="bg-[#0E1420] border-border font-mono text-sm" autoComplete="name" />
            </div>
          )}

          {mode !== "reset" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Email</Label>
              <Input data-testid="auth-email-input" type="email" required value={form.email}
                readOnly={!!invite?.valid}
                onChange={(e) => set("email", e.target.value)} placeholder="you@example.com"
                className="bg-[#0E1420] border-border font-mono text-sm" autoComplete="username" />
            </div>
          )}

          {mode !== "forgot" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">{mode === "reset" ? "New password" : "Password"}</Label>
              <Input data-testid="auth-password-input" type="password" required value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder={mode === "login" ? "••••••••" : "at least 8 characters"}
                className="bg-[#0E1420] border-border font-mono text-sm"
                autoComplete={mode === "login" ? "current-password" : "new-password"} />
            </div>
          )}

          {error && (
            <p data-testid="auth-error" className="text-xs text-rose-300 border border-rose-500/40 bg-rose-500/10 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          {sent && (
            <div data-testid="auth-reset-sent"
              className="text-xs text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded-md px-3 py-2 space-y-1">
              <p className="flex items-center gap-1.5 font-semibold"><MailCheck className="h-3.5 w-3.5" /> Check your inbox</p>
              <p className="text-emerald-200/70">
                If that address has an account, a reset link is on its way. The link expires in 60 minutes.
              </p>
            </div>
          )}

          <Button type="submit" data-testid="auth-submit-button" disabled={busy}
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : mode === "register" ? <UserPlus className="h-4 w-4 mr-1.5" />
              : mode === "login" ? <LogIn className="h-4 w-4 mr-1.5" />
              : <KeyRound className="h-4 w-4 mr-1.5" />}
            {mode === "register" ? (setupMode ? "Create owner account" : "Create account")
              : mode === "login" ? "Sign in"
              : mode === "forgot" ? "Email me a reset link" : "Set new password"}
          </Button>

          {mode === "login" && (
            <button type="button" data-testid="auth-forgot-link"
              onClick={() => { setMode("forgot"); setError(""); }}
              className="w-full text-xs font-mono text-slate-500 hover:text-cyan-300 transition-colors">
              Forgot your password?
            </button>
          )}

          {(mode === "forgot" || mode === "reset") && (
            <button type="button" data-testid="auth-back-to-login"
              onClick={() => { setMode("login"); setError(""); setSent(null); clean(); }}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-mono text-slate-500 hover:text-cyan-300 transition-colors">
              <ArrowLeft className="h-3 w-3" /> back to sign in
            </button>
          )}

          {canRegister && !setupMode && !invite?.valid && (mode === "login" || mode === "register") && (
            <button type="button" data-testid="auth-toggle-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="w-full text-xs font-mono text-slate-500 hover:text-cyan-300 transition-colors">
              {mode === "login" ? "No account? Create one" : "Already have an account? Sign in"}
            </button>
          )}

          {!canRegister && mode === "login" && (
            <p data-testid="signups-closed-note" className="flex items-center gap-1.5 text-[11px] font-mono text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5" /> sign-ups are invite-only — ask the owner to invite your email
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
